/**
 * recordsBrowserState
 *
 * Ephemeral UI state for the Records list-detail page: which record rows are
 * ticked and whether the user has escalated to "select all matching the current
 * filter". The rendered row order (for shift+click range selection) is NOT held
 * here — it lives in React (the merged Valtio-cache + Algolia list) and is passed
 * into the selection mutations at call time, so no derived state is mirrored in.
 *
 * Filters/query/sort are NOT held here — the URL is their source of truth
 * (see useRecordsBrowser). This proxy holds only selection so rows re-render
 * granularly via useSnapshot, following the ArTable selection model.
 *
 * Module singleton (one Records page per app). `resetRecordSelection` is called
 * when the active entity changes so selection never leaks across entities.
 * Mutations are direct Valtio writes — the intended pattern.
 */
import { proxy } from 'valtio';

/** Hard cap on explicit row selection; beyond this the user must "Select All". */
export const MAX_SELECTED_RECORD_IDS = 200;

type RecordSelectionState = {
    /** Map of record docId → selected, for granular per-row reactivity */
    selectedRows: Record<string, true>;
    /** Ordered selected docIds (fast iteration without Object.keys) */
    selectedRowIds: string[];
    /** docId → index in selectedRowIds, for O(1) removal */
    selectedRowIndexById: Record<string, number>;
    /** Last toggled row, anchor for shift+click range selection */
    lastClickedRowId: string | null;
};

type RecordsBrowserState = {
    selection: RecordSelectionState;
    /** When true, bulk actions target the whole filter, not selectedRowIds */
    selectAllMatching: boolean;
};

export const recordsBrowserState = proxy<RecordsBrowserState>({
    selection: {
        selectedRows: {},
        selectedRowIds: [],
        selectedRowIndexById: {},
        lastClickedRowId: null,
    },
    selectAllMatching: false,
});

const addToSelection = (id: string): void => {
    const selection = recordsBrowserState.selection;
    if (selection.selectedRows[id] === true) return;
    if (selection.selectedRowIds.length >= MAX_SELECTED_RECORD_IDS) return;
    selection.selectedRows[id] = true;
    selection.selectedRowIndexById[id] = selection.selectedRowIds.length;
    selection.selectedRowIds.push(id);
};

const removeFromSelection = (id: string): void => {
    const selection = recordsBrowserState.selection;
    if (selection.selectedRows[id] !== true) return;
    const idx = selection.selectedRowIndexById[id];
    if (idx !== undefined) {
        const lastIndex = selection.selectedRowIds.length - 1;
        const lastRowId = selection.selectedRowIds[lastIndex];
        if (idx !== lastIndex) {
            selection.selectedRowIds[idx] = lastRowId;
            selection.selectedRowIndexById[lastRowId] = idx;
        }
        selection.selectedRowIds.pop();
    }
    delete selection.selectedRowIndexById[id];
    delete selection.selectedRows[id];
};

/**
 * Toggle a row. With shiftKey held, selects the range between the last clicked
 * row and this one (using the supplied display order). Clears selectAllMatching
 * since the user is now curating an explicit set.
 */
export const toggleRecordSelection = (id: string, shiftKey: boolean, order: string[]): void => {
    recordsBrowserState.selectAllMatching = false;
    const selection = recordsBrowserState.selection;

    if (shiftKey && selection.lastClickedRowId !== null) {
        const lastIndex = order.indexOf(selection.lastClickedRowId);
        const currentIndex = order.indexOf(id);
        if (lastIndex !== -1 && currentIndex !== -1) {
            const start = Math.min(lastIndex, currentIndex);
            const end = Math.max(lastIndex, currentIndex);
            for (let i = start; i <= end; i++) addToSelection(order[i]);
        }
    } else if (selection.selectedRows[id] === true) {
        removeFromSelection(id);
    } else {
        addToSelection(id);
    }

    selection.lastClickedRowId = id;
};

/** Select every currently rendered row (capped). */
export const selectAllRenderedRecords = (order: string[]): void => {
    recordsBrowserState.selectAllMatching = false;
    for (const id of order) addToSelection(id);
    recordsBrowserState.selection.lastClickedRowId = null;
};

/** Clear all selection state (rows + select-all-matching). */
export const resetRecordSelection = (): void => {
    const selection = recordsBrowserState.selection;
    selection.selectedRows = {};
    selection.selectedRowIds = [];
    selection.selectedRowIndexById = {};
    selection.lastClickedRowId = null;
    recordsBrowserState.selectAllMatching = false;
};
